import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { accountsAPI, normalizeAccountList } from '../../data/accounts';
import { unwrap } from '../../data/api';

export const accountsKeys = {
  all: ['accounts'],
  list: (params) => [...accountsKeys.all, 'list', params || {}],
};


export function useFinancialAccountsQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: accountsKeys.list(params),
    queryFn: () =>
      accountsAPI.listAccounts(params).then((r) => normalizeAccountList(unwrap(r, []))),
    ...queryOptions,
  });
}

/** Active (non-archived) accounts, sharing the unfiltered list cache. */
export function useActiveAccountsQuery(queryOptions = {}) {
  return useFinancialAccountsQuery(undefined, {
    ...queryOptions,
    select: (accounts) =>
      Array.isArray(accounts) ? accounts.filter((a) => a.is_active) : [],
  });
}

function invalidateAccountCaches(qc) {
  qc.invalidateQueries({ queryKey: accountsKeys.all, exact: false });
}

/** Alias for useActiveAccountsQuery (kept for call sites that predate the rename). */
export const useAccountsQuery = useActiveAccountsQuery;

export function useCreateAccountMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      accountsAPI.createAccount(payload).then((r) => unwrap(r)),
    onSuccess: (created) => {
      if (created?.id) {
        qc.setQueriesData(
          { queryKey: accountsKeys.all, exact: false },
          (old) => {
            const list = normalizeAccountList(old);
            if (list.some((a) => a.id === created.id)) return old ?? list;
            return [...list, created];
          },
        );
      }
      invalidateAccountCaches(qc);
    },
  });
}

export function useUpdateAccountMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      accountsAPI.updateAccount(id, payload).then((r) => unwrap(r)),
    onSuccess: () => invalidateAccountCaches(qc),
  });
}

export function useArchiveAccountMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => accountsAPI.archiveAccount(id).then((r) => unwrap(r)),
    onSuccess: () => invalidateAccountCaches(qc),
  });
}

export function useRestoreAccountMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => accountsAPI.restoreAccount(id).then((r) => unwrap(r)),
    onSuccess: () => invalidateAccountCaches(qc),
  });
}
